import React, { useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Link2, Bold, List, Paperclip } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { apiErrorText } from '../../utils/apiErrorText';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SAFE = /^(https?:\/\/|mailto:|tel:|intake-file:)\S+$/i;
const normalise = (u) => {
  const v = u.trim();
  if (!v) return '';
  if (/^[\w.+-]+@[\w-]+\.[\w.]+$/.test(v)) return `mailto:${v}`;
  return (/^[a-z-]+:/i.test(v) ? v : `https://${v}`).replace(/\(/g, '%28').replace(/\)/g, '%29');
};

/** Opens an attached template document in a new tab (signed-in preview). */
export const openTemplateFile = async (fid) => {
  const win = window.open('', '_blank');
  try {
    const r = await axios.get(`${API}/candidate-intake-templates/files/${fid}`, { responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    if (win) win.location.href = url; else window.open(url, '_blank');
  } catch { win?.close(); toast.error('File not found'); }
};

const ToolBtn = ({ label, onClick, testId, children, ...rest }) => (
  <Button type="button" size="icon" variant="ghost" className="h-6 w-6 text-slate-500" title={label} aria-label={label}
    onMouseDown={(e) => e.preventDefault()} onClick={onClick} data-testid={testId} {...rest}>{children}</Button>
);

/** Text input/textarea with a small toolbar: bold, bullet list (multiline), link (optionally required) and attach document. */
export const LinkTextInput = ({ value, onChange, multiline = false, testId, className = '', ...rest }) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const fileRef = useRef(null);
  const sel = useRef([0, 0]);
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [required, setRequired] = useState(false);
  const [uploading, setUploading] = useState(false);
  const v = value || '';

  const grab = () => {
    const el = ref.current;
    sel.current = el ? [el.selectionStart ?? v.length, el.selectionEnd ?? v.length] : [v.length, v.length];
    return v.slice(...sel.current);
  };
  const replaceSel = (text) => { const [s, e] = sel.current; onChange(`${v.slice(0, s)}${text}${v.slice(e)}`); };
  const bold = () => { const s = grab(); replaceSel(`**${s || t('intakeTemplates.fmt.boldText')}**`); };
  const list = () => {
    const [s0, e0] = (grab(), sel.current);
    const s = v.lastIndexOf('\n', s0 - 1) + 1;
    const lines = v.slice(s, e0).split('\n').map((l) => (/^\s*[-•]\s/.test(l) ? l : `- ${l}`));
    onChange(`${v.slice(0, s)}${lines.join('\n')}${v.slice(e0)}`);
  };
  const openPopover = (o) => {
    if (o) { setLabel(grab()); setUrl(''); setRequired(false); }
    setOpen(o);
  };
  const href = normalise(url);
  const badText = /[[\]]/.test(label);
  const ok = SAFE.test(href) && label.trim() && !badText;
  const insert = (h = href, l = label) => {
    replaceSel(`[${l.trim()}](${h}${required ? ' "required"' : ''})`);
    setOpen(false);
  };
  const attach = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/candidate-intake-templates/files`, fd);
      const text = label.trim() || r.data.filename.replace(/\.[a-z0-9]+$/i, '');
      insert(r.data.href, text);
      toast.success(t('intakeTemplates.fmt.attached', { name: r.data.filename }));
    } catch (e) { toast.error(apiErrorText(e, t, 'intakeTemplates.fmt.attachFailed')); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };
  const Field = multiline ? Textarea : Input;
  return (
    <div className={className}>
      <Field ref={ref} value={v} onChange={(e) => onChange(e.target.value)} className={multiline ? 'text-sm' : 'h-8 text-sm'} data-testid={testId} {...rest} />
      <div className="mt-0.5 flex justify-end gap-0.5">
        <ToolBtn label={t('intakeTemplates.fmt.bold')} onClick={bold} testId={`${testId}-bold-btn`}><Bold className="h-3.5 w-3.5" /></ToolBtn>
        {multiline && <ToolBtn label={t('intakeTemplates.fmt.list')} onClick={list} testId={`${testId}-list-btn`}><List className="h-3.5 w-3.5" /></ToolBtn>}
        <Popover open={open} onOpenChange={openPopover}>
          <PopoverTrigger asChild>
            <Button type="button" size="icon" variant="ghost" className="h-6 w-6 text-slate-500" title={t('intakeTemplates.link.add')}
              aria-label={t('intakeTemplates.link.add')} onMouseDown={(e) => e.preventDefault()} data-testid={`${testId}-link-btn`}>
              <Link2 className="h-3.5 w-3.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 space-y-2 p-3" align="end" data-testid={`${testId}-link-popover`}>
            <p className="text-xs font-medium text-slate-700">{t('intakeTemplates.link.add')}</p>
            <Input className="h-8 text-sm" placeholder={t('intakeTemplates.link.text')} value={label} onChange={(e) => setLabel(e.target.value)} data-testid={`${testId}-link-text`} />
            <Input className="h-8 text-sm" placeholder="https://…" value={url} onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && ok) { e.preventDefault(); insert(); } }} data-testid={`${testId}-link-url`} />
            {badText && <p className="text-[11px] text-red-600">{t('intakeTemplates.link.noBrackets')}</p>}
            {url.trim() && !SAFE.test(href) && <p className="text-[11px] text-red-600">{t('intakeTemplates.link.invalid')}</p>}
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <Checkbox checked={required} onCheckedChange={(c) => setRequired(!!c)} data-testid={`${testId}-link-required`} />
              {t('intakeTemplates.link.required')}
            </label>
            <p className="text-[11px] text-slate-500">{t('intakeTemplates.link.hint')}</p>
            <div className="flex items-center justify-between gap-2">
              <Button type="button" size="sm" variant="outline" className="h-7 text-xs" disabled={uploading || badText}
                onClick={() => fileRef.current?.click()} data-testid={`${testId}-link-attach`}>
                <Paperclip className="h-3.5 w-3.5 mr-1" />{uploading ? t('intakeTemplates.fmt.uploading') : t('intakeTemplates.fmt.attach')}
              </Button>
              <input ref={fileRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.txt,image/*" onChange={(e) => attach(e.target.files?.[0])}
                data-testid={`${testId}-link-file-input`} />
              <Button type="button" size="sm" className="h-7 text-xs" disabled={!ok} onClick={() => insert()} data-testid={`${testId}-link-insert`}>{t('intakeTemplates.link.insert')}</Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export default LinkTextInput;
